import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm, Control, useWatch } from "react-hook-form";
import Image from "next/image";
import { destroyCookie, parseCookies, setCookie } from "nookies";
import RegistrationCheckoutForm from "./registration-checkout-form";
import RegistrationStatusPanel from "./registration-status-panel";
import CloseIcon from "./assets/close-icon";
import ArrowLeftIcon from "./assets/arrow-left-icon";
import Router from "next/router";
import DownloadIcon from "./assets/download-icon";
import type { EventParticipantField, EventRecord } from "../utils/events";
import { getPaymentIntentIdFromClientSecret } from "../utils/payment-intent-metadata";

const stripePromise = loadStripe(
  process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY as string
);

const handledPaymentReturns = new Set<string>();

const labelStyle = {
  fontFamily:
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
  color: "#30313d",
  fontSize: "0.93rem",
  fontWeight: "400",
  marginBottom: "0.25rem",
  transition:
    "transform 0.5s cubic-bezier(0.19, 1, 0.22, 1), opacity 0.5s cubic-bezier(0.19, 1, 0.22, 1)",
} as const;

const inputStyle = {
  border: "1px solid rgba(50, 50, 93, 0.1)",
  maxHeight: 47,
  width: "100%",
  boxShadow: "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
} as const;

const textareaStyle = {
  border: "1px solid rgba(50, 50, 93, 0.1)",
  width: "100%",
  minHeight: 96,
  boxShadow: "0px 1px 1px rgb(0 0 0 / 3%), 0px 3px 6px rgb(0 0 0 / 2%)",
} as const;

type Participant = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  priceId: string;
  customFields: Record<string, string>;
};

type FormValues = {
  participants: Participant[];
};

interface TotalProps {
  control: Control<FormValues>;
  prices: any[];
}

interface RegistrationFormProps {
  prices: any[];
  event: EventRecord;
  isClosed?: boolean;
}

type RegistrationPhase =
  | "form"
  | "checkout"
  | "confirming"
  | "success"
  | "error";

type RegistrationErrorKind = "payment" | "registration" | "checkout" | "general";

const buildEmptyParticipant = (eventFields: EventParticipantField[]): Participant => ({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  priceId: "",
  customFields: eventFields.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {} as Record<string, string>),
});

const getInputType = (type: EventParticipantField["type"]) => {
  if (type === "email") return "email";
  if (type === "phone") return "tel";
  if (type === "number") return "number";
  return "text";
};

const Total = ({ control, prices }: TotalProps) => {
  const participants = useWatch({ control, name: "participants" }) || [];
  const lineItems = participants.reduce(
    (acc: any, participant) => {
      const price = prices.find((p) => p.id === participant.priceId);
      if (!price) return acc;
      if (!acc[price.nickname]) {
        acc[price.nickname] = {
          quantity: 0,
          amount: price.unit_amount,
        };
      }
      acc[price.nickname].quantity += 1;
      acc.grandTotal += price.unit_amount;
      return acc;
    },
    { grandTotal: 0 }
  );

  if (lineItems.grandTotal === 0) return null;

  return (
    <div className="py-16 px-12 w-full">
      <div className="text-sm text-gray-600">Pay Glock Foundation</div>
      <div className="text-4xl">{`$${(lineItems.grandTotal / 100).toFixed(2)}`}</div>
      {Object.keys(lineItems)
        .filter((key) => key !== "grandTotal")
        .map((item) => (
          <div key={item} className="flex justify-between mt-8 text-gray-900">
            <div>
              <div>{item}</div>
              <div className="text-xs text-gray-500">
                Qty: {lineItems[item].quantity}
              </div>
            </div>
            <div className="min-w-[80px]">
              <div>{`$${(
                (lineItems[item].amount * lineItems[item].quantity) /
                100
              ).toFixed(2)}`}</div>
              {lineItems[item].quantity > 1 && (
                <div className="text-xs text-gray-500">{`$${(
                  lineItems[item].amount / 100
                ).toFixed(2)} each`}</div>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};

export default function RegistartionForm({
  prices,
  event,
  isClosed = false,
}: RegistrationFormProps) {
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [numSelected, setNumSelected] = useState(1);
  const [total, setTotal] = useState<number>();
  const [phase, setPhase] = useState<RegistrationPhase>("form");
  const [errorKind, setErrorKind] = useState<RegistrationErrorKind>("general");
  const [statusMessage, setStatusMessage] = useState("");
  const [statusDetail, setStatusDetail] = useState("");
  const [isSubmittingRegistration, setIsSubmittingRegistration] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");

  const defaultParticipant = useMemo(
    () => buildEmptyParticipant(event.participantFields),
    [event.participantFields]
  );
  const registrationDeadlineLabel = useMemo(() => {
    if (!event.registrationDeadline) {
      return "";
    }

    const parsed = dayjs(event.registrationDeadline);
    return parsed.isValid() ? parsed.format("MMMM D, YYYY") : event.registrationDeadline;
  }, [event.registrationDeadline]);

  const successEventDetails = useMemo(() => {
    const details = [
      event.eventDateLabel
        ? { label: event.eventDateFieldLabel, value: event.eventDateLabel }
        : null,
      event.eventTimeLabel
        ? { label: event.eventTimeFieldLabel, value: event.eventTimeLabel }
        : null,
      event.eventLocationLabel
        ? { label: "Location", value: event.eventLocationLabel }
        : null,
    ].filter(Boolean) as { label: string; value: string }[];

    return details;
  }, [
    event.eventDateFieldLabel,
    event.eventDateLabel,
    event.eventLocationLabel,
    event.eventTimeFieldLabel,
    event.eventTimeLabel,
  ]);

  const resetRegistrationFlow = useCallback(() => {
    destroyCookie(null, "participants");
    setCheckoutClientSecret("");
    setTotal(undefined);
    setPhase("form");
    setErrorKind("general");
    setStatusMessage("");
    setStatusDetail("");
    setCheckoutError("");
    Router.replace(`/register/${event.slug}`);
  }, [event.slug]);

  const completeRegistration = useCallback(
    async (paymentIntentId?: string) => {
      const cookies = parseCookies();
      const participants =
        cookies.participants && JSON.parse(cookies.participants);

      if (!paymentIntentId && !participants) {
        throw new Error("Registration details were not found. Please try again.");
      }

      const res = await fetch("/api/register", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          participants,
          eventSlug: event.slug,
          paymentIntentId: paymentIntentId || undefined,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(
          data.error ||
            "Registration details could not be saved. Please contact support."
        );
      }

      destroyCookie(null, "participants");
      Router.replace(`/register/${event.slug}`);
      setPhase("success");
      setStatusMessage("");
      setStatusDetail("");
    },
    [event.slug]
  );

  const showRegistrationError = useCallback(
    (kind: RegistrationErrorKind, message: string, detail = "") => {
      setErrorKind(kind);
      setStatusMessage(message);
      setStatusDetail(detail);
      setPhase("error");
    },
    []
  );

  useEffect(() => {
    if (!event.requiresPayment) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    const paymentIntentId = getPaymentIntentIdFromClientSecret(clientSecret);
    if (!paymentIntentId || handledPaymentReturns.has(paymentIntentId)) {
      return;
    }

    handledPaymentReturns.add(paymentIntentId);
    setPhase("confirming");

    stripePromise
      .then((stripe) => {
        if (!stripe) {
          throw new Error("Unable to verify payment. Please refresh and try again.");
        }

        return stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
          if (!paymentIntent) {
            throw new Error("Unable to verify payment. Please refresh and try again.");
          }

          switch (paymentIntent.status) {
            case "succeeded":
              return completeRegistration(paymentIntentId).catch((error: Error) => {
                showRegistrationError(
                  "registration",
                  "Your payment went through, but we couldn't finish your registration.",
                  error.message ||
                    "Please contact support so we can confirm your registration."
                );
              });
            case "processing":
              setPhase("confirming");
              setStatusMessage("Your payment is processing. We'll finish your registration once it clears.");
              return;
            case "requires_payment_method":
              showRegistrationError(
                "payment",
                "Your payment was not completed.",
                "Please try checkout again with a different payment method."
              );
              return;
            default:
              showRegistrationError(
                "payment",
                "We couldn't confirm your payment.",
                "Please try checkout again or contact support if the issue continues."
              );
          }
        });
      })
      .catch((error: Error) => {
        showRegistrationError(
          paymentIntentId ? "registration" : "payment",
          paymentIntentId
            ? "Your payment went through, but we couldn't finish your registration."
            : "We couldn't verify your payment.",
          error.message ||
            "Please contact support so we can confirm your registration."
        );
      });
  }, [completeRegistration, event.requiresPayment, showRegistrationError]);

  const { register, control, handleSubmit, getValues } = useForm<FormValues>({
    defaultValues: {
      participants: [defaultParticipant],
    },
  });

  const { fields, append, remove } = useFieldArray({
    name: "participants",
    control,
  });

  const handleBackClick = () => {
    setCheckoutClientSecret("");
    setTotal(undefined);
    setCheckoutError("");
    setPhase("form");
    setStatusMessage("");
    setStatusDetail("");
  };

  const onSubmit = async (formdata: FormValues) => {
    if (isClosed) {
      showRegistrationError(
        "general",
        "Registration for this event is closed.",
        registrationDeadlineLabel
          ? `The deadline was ${registrationDeadlineLabel}.`
          : ""
      );
      return;
    }

    if (isSubmittingRegistration) {
      return;
    }

    setStatusMessage("");
    setStatusDetail("");
    setIsSubmittingRegistration(true);

    const participants = getValues("participants");
    setCookie(null, "participants", JSON.stringify(participants), {
      path: "/",
    });

    if (!event.requiresPayment) {
      try {
        await completeRegistration();
      } catch (error: any) {
        showRegistrationError(
          "registration",
          "We couldn't save your registration.",
          error.message || "Please try again."
        );
      } finally {
        setIsSubmittingRegistration(false);
      }

      return;
    }

    try {
      const res = await fetch("/api/create-payment-intent", {
        method: "post",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: formdata.participants, eventSlug: event.slug }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        showRegistrationError(
          "checkout",
          "We couldn't start checkout.",
          data.error || "Please review your selections and try again."
        );
        setIsSubmittingRegistration(false);
        return;
      }

      setCheckoutClientSecret(data.clientSecret);
      setTotal(data.amount);
      setCheckoutError("");
      setPhase("checkout");
    } catch (error) {
      showRegistrationError(
        "checkout",
        "We couldn't start checkout.",
        "Please try again."
      );
    } finally {
      setIsSubmittingRegistration(false);
    }
  };

  const handleSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const length = parseInt(e.target.value, 10) || 0;
    if (numSelected > length) {
      Array.from({ length: fields.length - length }).forEach((_, index) => {
        const removeIndex = fields.length - index - 1;
        remove(removeIndex);
      });
    } else {
      Array.from({ length: length - fields.length }).forEach(() =>
        append(buildEmptyParticipant(event.participantFields))
      );
    }
    setNumSelected(length);
  };

  const handleRemove = (index: number) => {
    remove(index);
    setNumSelected(numSelected - 1);
  };

  const errorTitle = useMemo(() => {
    switch (errorKind) {
      case "payment":
        return "Payment not completed";
      case "registration":
        return "Registration issue";
      case "checkout":
        return "Checkout issue";
      default:
        return "Something went wrong";
    }
  }, [errorKind]);

  const hasLogo = Boolean(event.logoUrl);
  const logoSrc = event.logoUrl.startsWith("/")
    ? event.logoUrl
    : `/${event.logoUrl}`;

  const renderRightPanel = () => {
    if (phase === "success") {
      return (
        <RegistrationStatusPanel
          variant="success"
          title="You're registered!"
          message={`Thanks for registering for ${event.title}. A confirmation email is on its way.`}
          eventDetails={successEventDetails}
          primaryAction={{
            label: "Register another person",
            onClick: resetRegistrationFlow,
          }}
          secondaryAction={{
            label: "Back to homepage",
            onClick: () => Router.push("/"),
          }}
        />
      );
    }

    if (phase === "confirming") {
      return (
        <RegistrationStatusPanel
          variant="loading"
          title="Finishing your registration"
          message={
            statusMessage ||
            "Payment received. We're saving your registration and sending your confirmation email."
          }
        />
      );
    }

    if (phase === "error") {
      return (
        <RegistrationStatusPanel
          variant="error"
          title={errorTitle}
          message={statusMessage}
          detail={statusDetail}
          primaryAction={{
            label: errorKind === "registration" ? "Try again" : "Back to registration",
            onClick: resetRegistrationFlow,
          }}
        />
      );
    }

    if (phase === "checkout" && checkoutClientSecret) {
      return (
        <div>
          <button
            className="flex mb-4 items-center rounded-lg hover:bg-gray-100 p-1 -ml-2"
            onClick={handleBackClick}
            type="button"
          >
            <ArrowLeftIcon className="mr-2 w-5 h-5" /> Back to registration
          </button>
          <Elements
            options={{
              clientSecret: checkoutClientSecret,
              appearance: {
                theme: "stripe",
                variables: { colorPrimary: "#9496b7" },
              },
            }}
            stripe={stripePromise}
          >
            <RegistrationCheckoutForm
              total={total}
              preProps={
                numSelected === 1 ? getValues("participants")[0] : undefined
              }
              redirectRoute={`register/${event.slug}`}
              onPaymentError={(message) => setCheckoutError(message)}
            />
          </Elements>
          {checkoutError && (
            <div className="mt-4 rounded-lg border border-error/30 bg-error/10 px-4 py-3 text-left">
              <p className="font-semibold text-red-900">Payment not completed</p>
              <p className="mt-1 text-sm text-red-900">{checkoutError}</p>
            </div>
          )}
        </div>
      );
    }

    if (isClosed) {
      return (
        <RegistrationStatusPanel
          variant="error"
          title="Registration closed"
          message="Registration for this event is no longer open."
          detail={
            registrationDeadlineLabel
              ? `The deadline was ${registrationDeadlineLabel}.`
              : ""
          }
          primaryAction={{
            label: "Back to homepage",
            onClick: () => Router.push("/"),
          }}
        />
      );
    }

    return (
      <>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label style={labelStyle}>Number of Sign ups</label>
            <select
              className="rounded-md mb-4 p-3 bg-white max-w-xs"
              style={inputStyle}
              value={numSelected}
              onChange={handleSelect}
            >
              {Array.from({ length: 10 }).map((_, i) => (
                <option key={`option-${i + 1}`} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)}>
          {fields.map((field, index) => {
            return (
              <div
                key={field.id}
                className={`flex flex-col ${
                  index !== fields.length - 1 ? "border-b mb-8 pb-4" : ""
                }`}
              >
                <div className="flex justify-between">
                  <label style={labelStyle}>Person {index + 1}</label>
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="flex text-gray-500"
                    >
                      <CloseIcon className="text-gray-500 w-5 h-5 hover:text-gray-900" />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {event.requiresPayment && (
                    <select
                      {...register(`participants.${index}.priceId`, {
                        required: true,
                      })}
                      className="rounded-md p-3 bg-white col-span-2"
                      style={inputStyle}
                      defaultValue=""
                    >
                      {[{ nickname: event.selectLabel, id: "" }, ...prices].map(
                        (price, i) => (
                          <option
                            key={price.id || "placeholder"}
                            value={price.id}
                            disabled={i === 0}
                          >
                            {i === 0
                              ? price.nickname
                              : `$${price.unit_amount / 100} - ${price.nickname}`}
                          </option>
                        )
                      )}
                    </select>
                  )}
                  <input
                    placeholder="First name"
                    {...register(`participants.${index}.firstName`, {
                      required: true,
                    })}
                    className="rounded-md mb-4 p-3 bg-white"
                    style={inputStyle}
                  />
                  <input
                    placeholder="Last name"
                    {...register(`participants.${index}.lastName`, {
                      required: true,
                    })}
                    className="rounded-md mb-4 p-3 bg-white"
                    style={inputStyle}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <input
                    placeholder="Email"
                    type="email"
                    {...register(`participants.${index}.email`, {
                      required: true,
                    })}
                    className="rounded-md mb-4 p-3 bg-white"
                    style={inputStyle}
                  />
                  <input
                    placeholder="Phone number"
                    type="tel"
                    {...register(`participants.${index}.phone`, {
                      required: true,
                    })}
                    className="rounded-md mb-4 p-3 bg-white"
                    style={inputStyle}
                  />
                </div>
                {event.participantFields.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {event.participantFields.map((eventField) => {
                      const fieldName =
                        `participants.${index}.customFields.${eventField.key}` as const;
                      const commonClassName =
                        eventField.type === "textarea"
                          ? "rounded-md mb-4 p-3 bg-white col-span-2"
                          : "rounded-md mb-4 p-3 bg-white";

                      if (eventField.type === "select") {
                        return (
                          <div key={eventField.key} className="col-span-2">
                            <label style={labelStyle}>{eventField.label}</label>
                            <select
                              {...register(fieldName, {
                                required: eventField.required,
                              })}
                              className={commonClassName}
                              style={inputStyle}
                              defaultValue=""
                            >
                              <option value="" disabled>
                                {eventField.placeholder || `Select ${eventField.label}`}
                              </option>
                              {eventField.options.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        );
                      }

                      if (eventField.type === "textarea") {
                        return (
                          <div key={eventField.key} className="col-span-2">
                            <label style={labelStyle}>{eventField.label}</label>
                            <textarea
                              placeholder={eventField.placeholder || eventField.label}
                              {...register(fieldName, {
                                required: eventField.required,
                              })}
                              className={commonClassName}
                              style={textareaStyle}
                            />
                          </div>
                        );
                      }

                      return (
                        <div key={eventField.key}>
                          <label style={labelStyle}>{eventField.label}</label>
                          <input
                            placeholder={eventField.placeholder || eventField.label}
                            type={getInputType(eventField.type)}
                            {...register(fieldName, {
                              required: eventField.required,
                            })}
                            className={commonClassName}
                            style={inputStyle}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="flex flex-col w-full justify-center">
            <button
              type="submit"
              role="link"
              className={`btn ${
                isSubmittingRegistration
                  ? "border-primary bg-primary text-white opacity-100 hover:bg-primary disabled:border-primary disabled:bg-primary disabled:text-white disabled:opacity-100"
                  : ""
              }`}
              disabled={isSubmittingRegistration}
            >
              {isSubmittingRegistration ? (
                <span className="inline-flex items-center gap-2">
                  <div className="spinner" id="spinner"></div>
                  {event.requiresPayment ? "Loading checkout" : "Registering"}
                </span>
              ) : event.requiresPayment ? (
                "Continue to payment"
              ) : (
                "Register"
              )}
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <div className="flex flex-col lg:flex-row bg-white">
      <div className="lg:w-1/2 bg-neutral p-8 lg:min-h-screen relative">
        <div className="flex flex-col items-center lg:sticky lg:top-24">
          <h1 className="text-primary font-bold text-3xl mb-10 text-center">
            {event.title}
          </h1>
          {hasLogo && (
            <div className="px-8 bg-white py-8">
              <div className="aspect-video relative w-60">
                <Image src={logoSrc} alt="Logo" objectFit="contain" layout="fill" />
              </div>
            </div>
          )}
          {(event.eventDateLabel || event.eventTimeLabel || event.eventLocationLabel) && (
            <div className="mt-8 text-primary">
              {event.eventDateLabel && (
                <p>
                  <strong>{event.eventDateFieldLabel}:</strong> {event.eventDateLabel}
                </p>
              )}
              {event.eventTimeLabel && (
                <p>
                  <strong>{event.eventTimeFieldLabel}:</strong> {event.eventTimeLabel}
                </p>
              )}
              {event.eventLocationLabel && (
                <p>
                  <strong>Location:</strong> {event.eventLocationLabel}
                </p>
              )}
              {registrationDeadlineLabel && (
                <p>
                  <strong>Registration Deadline:</strong> {registrationDeadlineLabel}
                </p>
              )}
            </div>
          )}
          <div className="flex mt-4">
            {event.flyerUrl && (
              <a
                href={event.flyerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn border-2 border-white mr-2 rounded-full"
              >
                <DownloadIcon className="mr-2" /> Flyer
              </a>
            )}
            {event.rulesUrl && (
              <a
                href={event.rulesUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn border-2 border-white rounded-full"
              >
                <DownloadIcon className="mr-2" /> Rules
              </a>
            )}
          </div>
          {event.content && (
            <div
              className="prose prose-sm mt-8 max-w-none text-primary prose-p:text-primary prose-em:text-primary prose-strong:text-primary prose-li:text-primary"
              dangerouslySetInnerHTML={{ __html: event.content }}
            />
          )}
          {event.requiresPayment && <Total control={control} prices={prices} />}
        </div>
      </div>
      <div className="flex flex-col min-h-full lg:w-1/2 bg-white p-4">
        {renderRightPanel()}
      </div>
    </div>
  );
}
